import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { quizAttemptService } from '../services/learning/QuizAttemptService';
import { useAuth } from './AuthContext';

interface QuizContextType {
  isQuizInProgress: boolean;
  quizAttemptId: string | null;
  quizId: string | null;
  startQuiz: (attemptId: string, quizId: string, startedAt?: string) => void;
  endQuiz: () => void;
}

const QuizContext = createContext<QuizContextType | undefined>(undefined);

const QUIZ_STORAGE_KEY = 'quiz_attempt_id';
const QUIZ_ID_STORAGE_KEY = 'quiz_id';
const QUIZ_STARTED_AT_KEY = 'quiz_started_at';
const QUIZ_EXPIRATION_MINUTES = 20;

// Função helper para verificar se tentativa expirou
const isAttemptExpired = (startedAt: string): boolean => {
  const startTime = new Date(startedAt).getTime();
  const currentTime = Date.now();
  const diffMinutes = (currentTime - startTime) / (1000 * 60);
  return diffMinutes > QUIZ_EXPIRATION_MINUTES;
};

export const QuizProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [quizAttemptId, setQuizAttemptId] = useState<string | null>(() => {
    // Recuperar do sessionStorage ao inicializar e verificar expiração
    if (typeof window !== 'undefined') {
      const attemptId = sessionStorage.getItem(QUIZ_STORAGE_KEY);
      const startedAt = sessionStorage.getItem(QUIZ_STARTED_AT_KEY);
      
      // Se há tentativa e timestamp, verificar se expirou
      if (attemptId && startedAt) {
        if (isAttemptExpired(startedAt)) {
          // Limpar se expirada
          sessionStorage.removeItem(QUIZ_STORAGE_KEY);
          sessionStorage.removeItem(QUIZ_ID_STORAGE_KEY);
          sessionStorage.removeItem(QUIZ_STARTED_AT_KEY);
          return null;
        }
        return attemptId;
      }
    }
    return null;
  });
  const [quizId, setQuizId] = useState<string | null>(() => {
    // Recuperar do sessionStorage ao inicializar
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem(QUIZ_ID_STORAGE_KEY);
    }
    return null;
  });
  const checkingRef = useRef(false);

  const isQuizInProgress = quizAttemptId !== null;

  // Verificar tentativas ativas no backend quando autenticado
  useEffect(() => {
    if (!isAuthenticated || checkingRef.current) return;

    const checkActiveAttempt = async () => {
      checkingRef.current = true;
      try {
        const activeAttempt = await quizAttemptService.getActiveAttempt();
        if (activeAttempt) {
          // Verificar se tentativa expirou
          if (!activeAttempt.completedAt && isAttemptExpired(activeAttempt.startedAt)) {
            // Tentar marcar como abandonada no backend
            try {
              await quizAttemptService.abandonQuizAttempt(activeAttempt.id);
            } catch (err) {
              console.warn('Erro ao marcar tentativa como abandonada:', err);
            }
            // Limpar estado local
            setQuizAttemptId(null);
            setQuizId(null);
            if (typeof window !== 'undefined') {
              sessionStorage.removeItem(QUIZ_STORAGE_KEY);
              sessionStorage.removeItem(QUIZ_ID_STORAGE_KEY);
              sessionStorage.removeItem(QUIZ_STARTED_AT_KEY);
            }
          } else {
            // Tentativa válida, atualizar estado
            setQuizAttemptId(activeAttempt.id);
            setQuizId(activeAttempt.quizId);
            if (typeof window !== 'undefined') {
              sessionStorage.setItem(QUIZ_STORAGE_KEY, activeAttempt.id);
              sessionStorage.setItem(QUIZ_ID_STORAGE_KEY, activeAttempt.quizId);
              sessionStorage.setItem(QUIZ_STARTED_AT_KEY, activeAttempt.startedAt);
            }
          }
        } else {
          // Se não há tentativa ativa no backend, limpar estado local
          setQuizAttemptId(null);
          setQuizId(null);
          if (typeof window !== 'undefined') {
            sessionStorage.removeItem(QUIZ_STORAGE_KEY);
            sessionStorage.removeItem(QUIZ_ID_STORAGE_KEY);
            sessionStorage.removeItem(QUIZ_STARTED_AT_KEY);
          }
        }
      } catch (error) {
        console.error('Erro ao verificar tentativa ativa:', error);
        // Em caso de erro, manter estado local se existir
      } finally {
        checkingRef.current = false;
      }
    };

    checkActiveAttempt();
  }, [isAuthenticated]);

  // Verificar quando a janela ganha foco (usuário volta para a aba)
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleFocus = async () => {
      if (checkingRef.current) return;
      
      try {
        const activeAttempt = await quizAttemptService.getActiveAttempt();
        if (activeAttempt) {
          // Verificar se tentativa expirou
          if (!activeAttempt.completedAt && isAttemptExpired(activeAttempt.startedAt)) {
            // Tentar marcar como abandonada no backend
            try {
              await quizAttemptService.abandonQuizAttempt(activeAttempt.id);
            } catch (err) {
              console.warn('Erro ao marcar tentativa como abandonada:', err);
            }
            // Limpar estado
            setQuizAttemptId(null);
            setQuizId(null);
            if (typeof window !== 'undefined') {
              sessionStorage.removeItem(QUIZ_STORAGE_KEY);
              sessionStorage.removeItem(QUIZ_ID_STORAGE_KEY);
              sessionStorage.removeItem(QUIZ_STARTED_AT_KEY);
            }
          } else {
            // Tentativa válida, atualizar estado
            setQuizAttemptId(activeAttempt.id);
            setQuizId(activeAttempt.quizId);
            if (typeof window !== 'undefined') {
              sessionStorage.setItem(QUIZ_STORAGE_KEY, activeAttempt.id);
              sessionStorage.setItem(QUIZ_ID_STORAGE_KEY, activeAttempt.quizId);
              sessionStorage.setItem(QUIZ_STARTED_AT_KEY, activeAttempt.startedAt);
            }
          }
        } else {
          // Se não há tentativa ativa, limpar estado
          setQuizAttemptId(null);
          setQuizId(null);
          if (typeof window !== 'undefined') {
            sessionStorage.removeItem(QUIZ_STORAGE_KEY);
            sessionStorage.removeItem(QUIZ_ID_STORAGE_KEY);
            sessionStorage.removeItem(QUIZ_STARTED_AT_KEY);
          }
        }
      } catch (error) {
        console.error('Erro ao verificar tentativa ativa no foco:', error);
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isAuthenticated]);

  const startQuiz = (attemptId: string, quizIdParam: string, startedAt?: string) => {
    setQuizAttemptId(attemptId);
    setQuizId(quizIdParam);
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(QUIZ_STORAGE_KEY, attemptId);
      sessionStorage.setItem(QUIZ_ID_STORAGE_KEY, quizIdParam);
      if (startedAt) {
        sessionStorage.setItem(QUIZ_STARTED_AT_KEY, startedAt);
      }
    }
  };

  const endQuiz = () => {
    setQuizAttemptId(null);
    setQuizId(null);
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(QUIZ_STORAGE_KEY);
      sessionStorage.removeItem(QUIZ_ID_STORAGE_KEY);
      sessionStorage.removeItem(QUIZ_STARTED_AT_KEY);
    }
  };

  return (
    <QuizContext.Provider
      value={{
        isQuizInProgress,
        quizAttemptId,
        quizId,
        startQuiz,
        endQuiz,
      }}
    >
      {children}
    </QuizContext.Provider>
  );
};

export const useQuiz = (): QuizContextType => {
  const context = useContext(QuizContext);
  if (context === undefined) {
    throw new Error('useQuiz must be used within a QuizProvider');
  }
  return context;
};

